// Page « Mes parametres » — le canal de l avis, un reglage de la personne (mig 271).
// Le courriel est le defaut sans accord. Le texto exige un accord au texte affiche, enregistre
// avec sa date ; le retrait est un clic et ramene les avis au courriel. Tant qu aucun numero
// confirme n est connu, les avis partent par courriel meme apres l accord : la page le dit.
import { useState } from 'react';
import { Bell, Mail, MessageSquareText, RotateCw } from 'lucide-react';
import { GS_COMPONENTS } from '@gs/ui';
import { readApiErrorCode } from '@gs/api-client';
import { useTranslation } from '../i18n';
import { useCanalDeLAvis, useChoisirLeCanal } from '../hooks/useCanalDeLAvis';
import { VERSION_DU_TEXTE_AFFICHE, accordOffrable } from '../lib/accordTexto';

function codeDErreur(err: unknown): string | undefined {
  if (!err || typeof err !== 'object' || !('originalBody' in err)) return undefined;
  return readApiErrorCode((err as { originalBody?: unknown }).originalBody);
}

export default function MesParametresPage() {
  const { t, i18n } = useTranslation();
  const reglage = useCanalDeLAvis(true);
  const choisir = useChoisirLeCanal();
  const [luEtAccepte, setLuEtAccepte] = useState(false);

  const dateLongue = (iso: string | null): string =>
    iso
      ? new Date(iso).toLocaleDateString(i18n.language?.startsWith('en') ? 'en-CA' : 'fr-CA', { dateStyle: 'long' })
      : '—';

  const erreurDEcriture = (): string | null => {
    if (!choisir.isError) return null;
    const code = codeDErreur(choisir.error);
    if (code === 'NTF_CANAL_TEXTE_PERIME') return t('hub:parametres.erreur.textePerime');
    if (code === 'NTF_CANAL_MIGRATION_PENDING') return t('hub:parametres.erreur.indisponible');
    return t('hub:parametres.erreur.ecriture');
  };

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-xs dark:border-slate-700 dark:bg-slate-800">
        <div className="flex items-start gap-4">
          <div className={GS_COMPONENTS.iconContainer('brand')}>
            <Bell className={`h-5 w-5 ${GS_COMPONENTS.iconColor('brand')}`} aria-hidden />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-base font-semibold text-slate-800 dark:text-slate-100">{t('hub:parametres.canal.titre')}</h2>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">{t('hub:parametres.canal.description')}</p>
          </div>
        </div>

        {reglage.isLoading && (
          <div className="mt-5 space-y-3" aria-busy="true" aria-label={t('hub:parametres.chargement')}>
            <div className="h-5 w-2/3 animate-pulse rounded bg-slate-100 dark:bg-slate-700" />
            <div className="h-20 animate-pulse rounded-lg bg-slate-100 dark:bg-slate-700" />
          </div>
        )}

        {reglage.isError && (
          <div className="mt-5 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">
            <p>{t('hub:parametres.erreur.lecture')}</p>
            <button
              type="button"
              onClick={() => void reglage.refetch()}
              className={`${GS_COMPONENTS.btnSecondary} mt-3 inline-flex items-center gap-1.5 touch-manipulation`}
            >
              <RotateCw className="h-4 w-4" aria-hidden />
              {t('hub:parametres.reessayer')}
            </button>
          </div>
        )}

        {reglage.data && (
          <div className="mt-5 space-y-4">
            <div className="flex items-center gap-3 rounded-lg bg-slate-50 p-4 dark:bg-slate-900/40" role="status">
              {reglage.data.canal === 'sms' ? (
                <MessageSquareText className="h-5 w-5 shrink-0 text-slate-500" aria-hidden />
              ) : (
                <Mail className="h-5 w-5 shrink-0 text-slate-500" aria-hidden />
              )}
              <p className="text-sm text-slate-700 dark:text-slate-200">
                {reglage.data.canal === 'sms'
                  ? t('hub:parametres.etat.texto', { date: dateLongue(reglage.data.depuis) })
                  : reglage.data.source === 'choix'
                    ? t('hub:parametres.etat.courrielDepuis', { date: dateLongue(reglage.data.depuis) })
                    : t('hub:parametres.etat.courrielDefaut')}
              </p>
            </div>

            {reglage.data.canal === 'sms' && (
              <>
                <p className="text-sm text-slate-600 dark:text-slate-400">{t('hub:parametres.numeroAConfirmer')}</p>
                <button
                  type="button"
                  disabled={choisir.isPending}
                  onClick={() => choisir.mutate({ canal: 'email' })}
                  className={`${GS_COMPONENTS.btnDangerOutline} touch-manipulation`}
                >
                  {t('hub:parametres.retirer')}
                </button>
              </>
            )}

            {reglage.data.canal === 'email' && accordOffrable(reglage.data.versionCouranteDuTexte) && (
              <div className="space-y-3 rounded-lg border border-slate-200 p-4 dark:border-slate-700">
                <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">{t('hub:parametres.accord.titre')}</h3>
                <p className="text-sm text-slate-600 dark:text-slate-400">{t('hub:parametres.accord.finalite')}</p>
                <p className="text-sm text-slate-600 dark:text-slate-400">{t('hub:parametres.accord.numero')}</p>
                <p className="text-sm text-slate-600 dark:text-slate-400">{t('hub:parametres.accord.frais')}</p>
                <p className="text-sm text-slate-600 dark:text-slate-400">{t('hub:parametres.accord.retrait')}</p>
                <label className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-200 touch-manipulation">
                  <input
                    type="checkbox"
                    checked={luEtAccepte}
                    onChange={(e) => setLuEtAccepte(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-slate-300"
                  />
                  <span>{t('hub:parametres.accord.case')}</span>
                </label>
                <button
                  type="button"
                  disabled={!luEtAccepte || choisir.isPending}
                  onClick={() => choisir.mutate({ canal: 'sms', versionDuTexte: VERSION_DU_TEXTE_AFFICHE })}
                  className={`${GS_COMPONENTS.btnAction} touch-manipulation`}
                >
                  {t('hub:parametres.accepter')}
                </button>
              </div>
            )}

            {reglage.data.canal === 'email' && !accordOffrable(reglage.data.versionCouranteDuTexte) && (
              <p className="text-sm text-slate-600 dark:text-slate-400">{t('hub:parametres.erreur.texteEnMiseAJour')}</p>
            )}

            {erreurDEcriture() && (
              <p role="alert" className="text-sm text-red-700 dark:text-red-300">
                {erreurDEcriture()}
              </p>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
